import { FC, useEffect, useMemo, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import { ProductTemplate, fetchProductTemplates } from '../../api/productTemplates';
import { runSyncJob } from '../../api/syncData';

interface TemplatesState {
  records: ProductTemplate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface TemplatesPanelProps {
  onSelect?: (id: string) => void;
}

const TemplatesPanel: FC<TemplatesPanelProps> = ({ onSelect }) => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<TemplatesState>({
    records: [],
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchProductTemplates({ page, limit, search: debouncedSearch || undefined })
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setState({
          records: response.data,
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load templates.');
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, debouncedSearch, refreshCounter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(state.total / limit || 1)),
    [state.total, limit],
  );

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  const handleSync = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    try {
      await runSyncJob('productTemplates');
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync templates.';
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search templates..."
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
        />

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync from ERP'}
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Description</th>
              <th className="px-5 py-3 font-semibold">Attributes</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={5}>
                  Loading templates...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={5}>
                  {error}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={5}>
                  No templates found.
                </td>
              </tr>
            ) : (
              state.records.map((template) => (
                <tr key={template.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{template.name}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {template.description?.length ? template.description : '—'}
                  </td>
                  <td className="px-5 py-3 text-slate-500">{template.attributes?.length ?? 0}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {new Date(template.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-primary transition hover:bg-primary/10"
                      onClick={() => onSelect?.(template.id)}
                    >
                      👁️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(state.page - 1) * limit + Math.min(limit, state.records.length)} of {state.total}{' '}
            templates
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} (20 / page)</span>
        }
      />
    </div>
  );
};

export default TemplatesPanel;
